import { computed } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import * as DocumentManagement from "../DocumentManagement";
import * as DocumentReader from "../DocumentReader";
import { IconType, MimeTypeIcon } from "../Icons/MimeTypeIcon";
import { AppStore } from "../Stores/AppStore";
import { DialogStore } from "../Stores/DialogStore";
import { FavouriteDataStore } from "../Stores/FavouriteDataStore";
import { Strings } from "../Stores/StringsStore";
import { Urls } from "../Urls";
import { getDisplayMimetype } from "../Utilities";


export class Download {
	static requestDownload = (fileResult: DocumentReader.FileResultInfo) => {
		let request = new DocumentReader.DownloadRequest();
		request.fileId = fileResult.guid;
		request.documentId = fileResult.documentId;
		DocumentReader.requestDownloadAsync(request).then((result: DocumentReader.OfflineStatus) => {
			fileResult.status = result;
		}).catch(error => {
			DialogStore.showError(Strings.general.errorDialogTitle, error);
			fileResult.status = DocumentReader.OfflineStatus.Error;
		});
	}

	static requestUpdate = (fileResult: DocumentReader.FileResultInfo) => {
		let request = new DocumentReader.UpdateRequest();
		request.downloadRequest = new DocumentReader.DownloadRequest();
		request.downloadRequest.fileId = fileResult.availableFileUpdate ? fileResult.availableFileUpdate.guid : fileResult.guid;
		request.downloadRequest.documentId = fileResult.documentId;
		request.oldFile = new DocumentReader.DeleteQuery();
		request.oldFile.guid = fileResult.guid;
		request.oldFile.mimeType = fileResult.mimeType;
		DocumentReader.requestUpdateAsync(request).then((result: DocumentReader.OfflineStatus) => {
			fileResult.status = result;
		}).catch(error => {
			DialogStore.showError(Strings.general.errorDialogTitle, error);
			fileResult.status = DocumentReader.OfflineStatus.Error;
		});
	}

	static cancelDownload = (fileResult: DocumentReader.FileResultInfo) => {
		DocumentReader.cancelDownloadAsync(fileResult.guid)
			.catch(error => {
				DialogStore.showError(Strings.general.errorDialogTitle, error);
			});
	}
}

export interface IDocumentNameElement {
	documentId: string;
	language: string;
	guid: string;
	name: string;
	mimeType: string;
	hideMimeTypeIcon?: boolean;
	testName?: string;
	testId?: string;
	fileLinkGuid?: string;
	isCoverAvailable?: boolean;
	onClick?: React.EventHandler<React.MouseEvent<any>>;
}

export class DocumentNameElement extends React.Component<IDocumentNameElement, {}> {

	render() {
		return <a onClick={this.props.onClick} className="link-document is--mimetype-link">
			<MimeTypeIcon mimeType={getDisplayMimetype(this.props.mimeType)} iconType={IconType.Png16} />
			<span>
				{this.props.name}
			</span>
		</a>;
	}
}

export interface IDocumentsView {
	scrollPercentage: number;
	scrollLock: boolean;
	skip: number;
	top: number;
	sortBy: DocumentReader.SortBy[];
	searchResult: DocumentReader.DocumentResult;
	triggerSearch: any;
	isBusy: boolean;
	noResultMsg: string;
	favouriteChange: any;
	showOnlyName: boolean;
}

export function handleLoadMore(o: any, store: IDocumentsView) {
	let tracker = o.target;
	let totalLimit = parseInt(tracker.scrollHeight) - parseInt(tracker.clientHeight);
	let limit = totalLimit - (totalLimit * store.scrollPercentage / 100);
	if (o.target.scrollTop > limit) {
		if (store.scrollLock) {
			store.skip += 50;
			if (store.searchResult.documents.length % 100 === 0 && store.scrollPercentage > 8) {
				store.scrollPercentage -= 8;
			}
			store.triggerSearch(false, true);
		}
	}
}

export interface IDocumentsViewEntryPropsBase {
	store?: IDocumentsView;
	fileResult: DocumentReader.FileResultInfo;
	applyUpdatesToModel: boolean;
	isSelected?: boolean;
	isHovering?: boolean;
}

@observer
export class DocumentsViewEntry<P extends IDocumentsViewEntryPropsBase> extends React.Component<P, {}> {
	timeoutId: number;
	initialStatus: DocumentReader.OfflineStatus = this.props.fileResult.status;
	cancelled: boolean;

	private installTimer() {
		let status = this.props.fileResult.status;
		if (status === DocumentReader.OfflineStatus.Waiting || status === DocumentReader.OfflineStatus.Downloading) { //keep checking status if downloading
			this.timeoutId = window.setInterval(() => { this.checkStatus(this.props.applyUpdatesToModel) }, 1000);
		}
	}

	private uninstallTimer() {
		if (this.timeoutId) {
			window.clearInterval(this.timeoutId);
		}
	}

	componentDidUpdate() {
		this.uninstallTimer();
		this.installTimer();
	}

	componentDidMount() {
		this.installTimer();
	}

	componentWillUnmount() {
		this.uninstallTimer();
	}

	handleRequestDownload = () => {
		let status = this.props.fileResult.status;
		switch (status) {
			case DocumentReader.OfflineStatus.None:
				this.cancelled = false;
				return Download.requestDownload(this.props.fileResult);
			case DocumentReader.OfflineStatus.Update:
				this.cancelled = false;
				return Download.requestUpdate(this.props.fileResult);
			case DocumentReader.OfflineStatus.Waiting:
			case DocumentReader.OfflineStatus.Downloading:
				this.cancelled = true;
				return Download.cancelDownload(this.props.fileResult);
			case DocumentReader.OfflineStatus.Error:
				this.cancelled = false;
				if (this.initialStatus === DocumentReader.OfflineStatus.Update) {
					return Download.requestUpdate(this.props.fileResult);
				}
				else {
					return Download.requestDownload(this.props.fileResult);
				}
			default:
				return;
		}
	}

	private checkStatus(applyUpdate: boolean) {
		let query = new DocumentReader.StatusQuery();
		let availableFileUpdate = this.props.fileResult.availableFileUpdate;
		let queryId = availableFileUpdate ? availableFileUpdate.guid : this.props.fileResult.guid;
		let queryMimeType = availableFileUpdate ? availableFileUpdate.mimeType : this.props.fileResult.mimeType;
		query.fileId = queryId;
		query.mimeType = queryMimeType;
		DocumentReader.getDownloadStatusAsync(query).then((result: DocumentReader.OfflineStatus) => {
			if (this.props.fileResult.status === DocumentReader.OfflineStatus.Downloading && result === DocumentReader.OfflineStatus.None && !this.cancelled) {
				this.props.fileResult.status = DocumentReader.OfflineStatus.Error;
			}
			else {
				this.props.fileResult.status = result;
			}
			if (result === DocumentReader.OfflineStatus.Offline && applyUpdate && this.initialStatus === DocumentReader.OfflineStatus.Update) { //apply file update after download finishes
				DocumentReader.updateFileResultInfoAsync(queryId).then((result: DocumentReader.FileResultInfo) => {
					this.props.fileResult.availableFileUpdate = result.availableFileUpdate;
					this.props.fileResult.collectionId = result.collectionId;
					this.props.fileResult.documentId = result.documentId;
					this.props.fileResult.fileLinkGuid = result.fileLinkGuid;
					this.props.fileResult.guid = result.guid;
					this.props.fileResult.isCoverAvailable = result.isCoverAvailable;
					this.props.fileResult.language = result.language;
					this.props.fileResult.languageName = result.languageName;
					this.props.fileResult.mimeType = result.mimeType;
					this.props.fileResult.region = result.region;
					this.props.fileResult.size = result.size;
					this.props.fileResult.status = result.status;
					this.initialStatus = result.status;
					this.props.fileResult.values = result.values;
				}).catch(error => {
					this.props.fileResult.status = DocumentReader.OfflineStatus.Error;
					DialogStore.showError(Strings.general.errorDialogTitle, error);
				});
			}
		}).catch(error => {
			this.props.fileResult.status = DocumentReader.OfflineStatus.Error;
			DialogStore.showError(Strings.general.errorDialogTitle, error);
		});
	}

	@computed get favouriteState(): number {
		if (FavouriteDataStore.favouriteDocs.length > 0) {
			return FavouriteDataStore.isFavourite(this.props.fileResult.documentId, this.props.fileResult.language) ? 2 : 1;
		} else {
			return 1;
		}
	}

	handleAddFavourites = () => {
		if (FavouriteDataStore.isFavourite(this.props.fileResult.documentId, this.props.fileResult.language)) {
			FavouriteDataStore.removeFromFavourite(this.props.fileResult.documentId, this.props.fileResult.language);
		}
		else {
			FavouriteDataStore.addToFavourite(this.props.fileResult.documentId, this.props.fileResult.language);
		}
		this.props.store.favouriteChange(this.props.fileResult.documentId);
	}

	handleEditDocument = () => {
		DocumentManagement.getCollectionHierarchyAsync(this.props.fileResult.collectionId).then((result: DocumentManagement.CollectionHierarchy) => {
			let path = `${Urls.collections}/${this.props.fileResult.collectionId}/documents/${this.props.fileResult.documentId}`;
			let treepath = `?treepath=${result.parentGuids.join(",")}`;
			if (treepath) {
				treepath += `,${this.props.fileResult.collectionId}`;
				path += treepath;
			}
			AppStore.keepOnPage(path, true);
		});
	}
}